import React, { useState, useMemo } from 'react';
import { InvitationCustomizations } from '../../_types/invitation-design';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import { convertToInvitationDesign } from '../../_utils/design-converter';

interface DesignTestPanelProps {
  designV1?: InvitationCustomizations;
  designV2?: InvitationDesign;
  invitationTypeId?: string;
  isOpen: boolean;
  onClose?: () => void;
  onApplyDesign?: (design: InvitationDesign) => void;
}

export const DesignTestPanel: React.FC<DesignTestPanelProps> = ({
  designV1,
  designV2,
  invitationTypeId = 'test-type',
  isOpen,
  onClose,
  onApplyDesign
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['stats']));
  const [editedJson, setEditedJson] = useState<{ [key: string]: string }>({});
  const [jsonErrors, setJsonErrors] = useState<{ [key: string]: string }>({});

  // Convert V1 to V2 if needed
  const design = useMemo(() => {
    if (designV2) return designV2;
    if (designV1) return convertToInvitationDesign(designV1, invitationTypeId);
    return null;
  }, [designV1, designV2, invitationTypeId]);

  if (!isOpen || !design) return null;

  const jsonString = JSON.stringify(design, null, 2);
  const jsonSize = (new Blob([jsonString]).size / 1024).toFixed(2);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonString);
    // Could add a toast notification here
  };

  const handleJsonEdit = (key: string, value: string) => {
    setEditedJson(prev => ({ ...prev, [key]: value }));
    
    // Validate JSON
    try {
      JSON.parse(value);
      setJsonErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    } catch (e) {
      setJsonErrors(prev => ({ ...prev, [key]: 'Invalid JSON: ' + (e as Error).message }));
    }
  };

  const handleApplyChanges = () => {
    if (!onApplyDesign || !design) return;

    try {
      // Merge all edited sections into the design
      let updatedDesign = { ...design };

      // Apply template changes
      if (editedJson.template) {
        updatedDesign.template = JSON.parse(editedJson.template);
      }

      // Apply overlays changes
      if (editedJson.overlays) {
        updatedDesign.overlays = JSON.parse(editedJson.overlays);
      }

      // Apply content changes
      if (editedJson.content) {
        updatedDesign.content = JSON.parse(editedJson.content);
      }

      // Apply metadata changes
      if (editedJson.metadata) {
        updatedDesign.metadata = JSON.parse(editedJson.metadata);
      }

      // Apply styling changes (composite from theme data)
      if (editedJson.styling) {
        const styling = JSON.parse(editedJson.styling);
        updatedDesign.template = {
          ...updatedDesign.template,
          theme: styling.theme || updatedDesign.template.theme,
          defaultTextStyle: styling.defaultTextStyle || updatedDesign.template.defaultTextStyle,
          background: styling.background || updatedDesign.template.background,
          border: styling.border || updatedDesign.template.border
        };
      }

      // Apply full JSON if edited
      if (editedJson.full) {
        updatedDesign = JSON.parse(editedJson.full);
      }

      onApplyDesign(updatedDesign);
      
      // Clear edited state after successful apply
      setEditedJson({});
      setJsonErrors({});
    } catch (e) {
      alert('Failed to apply changes: ' + (e as Error).message);
    }
  };

  const renderJsonSection = (title: string, data: any, key: string) => {
    const isExpanded = expandedSections.has(key);
    const currentJson = editedJson[key] || JSON.stringify(data, null, 2);
    const hasError = !!jsonErrors[key];
    const isEdited = !!editedJson[key];

    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(key)}
          className="flex items-center justify-between w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-750 rounded transition-colors"
        >
          <span className="text-sm font-medium text-gray-200">
            {title}
            {isEdited && <span className="ml-2 text-xs text-yellow-400">(edited)</span>}
            {hasError && <span className="ml-2 text-xs text-red-400">(error)</span>}
          </span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        {isExpanded && (
          <div>
            <textarea
              className={`mt-2 p-3 bg-gray-900 rounded text-xs text-gray-300 overflow-x-auto w-full font-mono resize-vertical min-h-[200px] max-h-[600px] border ${
                hasError ? 'border-red-500' : isEdited ? 'border-yellow-500' : 'border-gray-700'
              }`}
              value={currentJson}
              onChange={(e) => handleJsonEdit(key, e.target.value)}
              spellCheck={false}
            />
            {hasError && (
              <p className="mt-1 text-xs text-red-400">{jsonErrors[key]}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`fixed right-0 top-0 h-full bg-gray-950 text-white shadow-2xl transition-all duration-300 z-50 flex flex-col ${
        isMinimized ? 'w-12' : 'w-96'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <h3 className={`font-semibold ${isMinimized ? 'hidden' : ''}`}>Design Test Panel</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-gray-800 rounded transition-colors"
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMinimized ? 'M13 5l7 7-7 7' : 'M11 19l-7-7 7-7'}
              />
            </svg>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-800 rounded transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {!isMinimized && (
        <div className="flex-1 overflow-y-auto p-4 h-[calc(100vh-4rem)]">
          {/* Design Stats */}
          {renderJsonSection(
            'Design Stats',
            {
              version: design.version,
              contentRows: design.content.length,
              overlays: design.overlays?.length || 0,
              sizeKB: jsonSize,
              type: designV2 ? 'V2 Design' : 'Converted from V1'
            },
            'stats'
          )}

          {/* Template Config */}
          {renderJsonSection('Template Configuration', design.template, 'template')}

          {/* Overlays */}
          {design.overlays && design.overlays.length > 0 && (
            renderJsonSection(`Overlay Images (${design.overlays.length})`, design.overlays, 'overlays')
          )}

          {/* Content Rows */}
          {renderJsonSection(`Content Rows (${design.content.length})`, design.content, 'content')}

          {/* Metadata */}
          {design.metadata && renderJsonSection('Metadata', design.metadata, 'metadata')}

          {/* Theme & Styling */}
          {renderJsonSection('Theme & Styling', {
            theme: design.template.theme,
            defaultTextStyle: design.template.defaultTextStyle,
            background: design.template.background,
            border: design.template.border
          }, 'styling')}

          {/* Action Buttons */}
          <div className="mt-6 pt-4 border-t border-gray-800 space-y-2">
            {onApplyDesign && Object.keys(editedJson).length > 0 && (
              <>
                <button
                  onClick={handleApplyChanges}
                  disabled={Object.keys(jsonErrors).length > 0}
                  className={`w-full px-4 py-2 rounded transition-colors text-sm font-medium ${
                    Object.keys(jsonErrors).length > 0
                      ? 'bg-gray-600 cursor-not-allowed opacity-50'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Apply Changes ({Object.keys(editedJson).length} section{Object.keys(editedJson).length > 1 ? 's' : ''})
                </button>
                <button
                  onClick={() => {
                    setEditedJson({});
                    setJsonErrors({});
                  }}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded transition-colors text-sm font-medium"
                >
                  Reset Changes
                </button>
              </>
            )}
            <button
              onClick={copyToClipboard}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors text-sm font-medium"
            >
              Copy JSON to Clipboard
            </button>
          </div>

          {/* Full JSON (collapsible) */}
          <div className="mt-4">
            {renderJsonSection('Full JSON', design, 'full')}
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignTestPanel;